import {Avatar, Box, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {FormattedMessage} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {legacyActionsSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {
    avatarSx,
    cardSx,
    factEmptySx,
    factLabelSx,
    factNoteSx,
    factSx,
    factValueSx,
    factsSx,
    footerCompactSx,
    footerSx,
    headerSx,
    subtitleSx,
    titleSx,
    typePillSx,
} from './UserIdentityCard.sx';
import {UserIdentityCardProps} from './UserIdentityCard.types';

const initials = (title: string) =>
    title
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('') || '?';

/**
 * Who this person is, in one card, whatever kind of person they are.
 *
 * The page used to draw three of these: one component for a company, another for a driver, and for
 * an employee ninety lines written into the page body and styled from another page's file. They said
 * the same things in three layouts, and only the employee's role was hardcoded rather than read.
 * What varies by type now is which four facts are passed in.
 */
export const UserIdentityCard: FC<UserIdentityCardProps> = ({data, facts, actions, footer, title, typeLabel}) => {
    const contacts = useMemo(
        () => [data.emailAddress, data.phoneNumber].filter(Boolean).join(' · '),
        [data.emailAddress, data.phoneNumber]
    );
    const {isCompact} = useScreenTier();

    /** The account type, drawn once and placed differently: beside the name, or as the status. */
    const typePill = typeLabel ? (
        <Box component="span" sx={typePillSx}>
            <FormattedMessage id={typeLabel}/>
        </Box>
    ) : null;

    if (isCompact) {
        return (
            <>
                <RecordIdentityCard
                    person
                    icon={(
                        <Avatar
                            src={data.avatar?.url}
                            alt={title}
                        >
                            {initials(title)}
                        </Avatar>
                    )}
                    title={title}
                    subtitle={contacts}
                    status={typePill}
                    // The same four facts the desktop card states, including the "not set" placeholder
                    // that keeps the grid even when a person has nothing in a slot.
                    facts={facts.map((fact) => ({
                        id: fact.label,
                        label: <FormattedMessage id={fact.label}/>,
                        value: fact.value ? (
                            <>
                                {fact.value}
                                {fact.note && <Typography sx={factNoteSx}>{fact.note}</Typography>}
                            </>
                        ) : (
                            <FormattedMessage id={fact.emptyLabel ?? 'notSet'}/>
                        ),
                    }))}
                />
                {footer && <Box sx={footerCompactSx}>{footer}</Box>}
                {actions && (
                    <RecordActionBar>
                        <Box sx={legacyActionsSx}>{actions}</Box>
                    </RecordActionBar>
                )}
            </>
        );
    }

    return (
        <Box sx={cardSx}>
            <Box sx={headerSx}>
                <Avatar
                    src={data.avatar?.url}
                    alt={title}
                    sx={avatarSx}
                >
                    {initials(title)}
                </Avatar>
                <Box sx={{minWidth: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                        <Typography sx={titleSx}>{title}</Typography>
                        {typePill}
                    </Box>
                    {/* The contacts move up here from the facts grid: they are how you reach the
                        person, which is closer to their name than to their addresses. */}
                    {contacts && <Typography sx={subtitleSx}>{contacts}</Typography>}
                </Box>

                <Box sx={{flex: '1 1 auto'}}/>

                {actions}
            </Box>

            <Box sx={factsSx}>
                {facts.map((fact, index) => (
                    <Box
                        key={fact.label}
                        sx={factSx(index, facts.length)}
                    >
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id={fact.label}/>
                        </Typography>
                        {fact.value ? (
                            <Typography component="div" sx={factValueSx}>{fact.value}</Typography>
                        ) : (
                            <Typography sx={factEmptySx}>
                                <FormattedMessage id={fact.emptyLabel ?? 'notSet'}/>
                            </Typography>
                        )}
                        {fact.note && <Typography sx={factNoteSx}>{fact.note}</Typography>}
                    </Box>
                ))}
            </Box>

            {footer && <Box sx={footerSx}>{footer}</Box>}
        </Box>
    );
};
